import { useFlightAccess } from '@/app/flightAccess';
import { InfoIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import { describeAccess, useAccessNames } from '@/lib/flightAccess';
import { isRestricted } from '@/types/flightAccess';
import type { FlightAccess } from '@/types/flightAccess';
import styles from './FlightAccessNote.module.css';

/**
 * "Your flights: Air Astana flights to or from Astana" — for a restricted user, on
 * every screen that shows flights.
 *
 * Nothing at all for anyone else, which is most people: a user who sees every flight
 * gains nothing from being told so on every screen.
 *
 * It describes; it does not filter. The rows on the screen were narrowed by the
 * server before they arrived, and this is only the reason, said once, so that a board
 * with fewer flights than a colleague's reads as an access rather than a fault.
 */
export const FlightAccessNote = () => {
  const { access } = useFlightAccess();

  // Split so the dictionaries the sentence is made from are only asked for when there
  // is a sentence to make.
  return access !== null && isRestricted(access) ? <RestrictedNote access={access} /> : null;
};

const RestrictedNote = ({ access }: { access: FlightAccess }) => {
  const { t } = useTranslation();
  const names = useAccessNames(access.airports);
  const description = describeAccess(access, t, names);

  return (
    // A note, not a status: it does not change while the reader is on the page, so
    // announcing it would only interrupt. The title carries the whole sentence — the
    // line may be cut short — and what to do about it.
    <p className={styles.note} title={`${description}\n${t('flightAccess.yoursHint')}`}>
      <InfoIcon className={styles.icon} />
      <span className={styles.label}>{t('flightAccess.yours')}</span>
      <span className={styles.text}>{description}</span>
    </p>
  );
};
